import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { formatiereZahl } from './WertEinheit';
import {
  anzeigezeilen, fehlstellenText, fehlstellenVermerk, fussArt, fussText, fussWert,
  gruppenBeschriftung, gruppiereZeilen, kopfzeilen,
  type Anzeigezeile, type FussDefinition, type Fusswert, type Gruppe, type Gruppierung,
} from './tabellengruppen';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DATENTABELLE (Auftrag 17.09.2026, Abschnitt 10 - das wichtigste Muster)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Tabellen sind in einer Engineering-Anwendung extrem wichtig und müssen
 *    besonders gut umgesetzt werden. […] Tabellenname, Trefferanzahl, Suche,
 *    Filter, Spaltenauswahl, Export, Sortierung, Mehrfachauswahl,
 *    Pagination, Kontextmenü, Status, Sticky Header, optional Sticky First
 *    Column. […] Status nicht ausschließlich über Farbe. […] Kein unnötiges
 *    vollständiges Rendern tausender DOM-Zeilen."
 *
 * WAS DER BESTAND HEUTE TUT (geprüft, nicht vermutet)
 * ---------------------------------------------------
 * - `Raumbuch.tsx`, `Tuerbuch.tsx`: `<table>` von Hand, Spaltenpolsterung
 *   als `style`-Objekt an jeder einzelnen Zelle.
 * - `ElektroVerteiler.tsx`: Zahlen mit `toLocaleString('de-DE')`, Einheit in
 *   der Überschrift („Bedarf in kW"), Überlast als roter Zellentext.
 * - `Koordination.tsx`: Blättern über den Server (`seite`, `proSeite`) und
 *   Filter in der Adresszeile (`useSearchParams`).
 * - **Virtualisierung gibt es nirgends.** Lange Listen werden mit
 *   `.slice(0, 200|300)` abgeschnitten und mit einem Knopf „alle zeigen"
 *   vollständig in das Dokument gestellt (24 Fundstellen). Das ist der
 *   Punkt, an dem dieses Muster die vorhandene Lösung nicht nur erhält,
 *   sondern verbessert: ab `virtualisierenAb` Zeilen (Vorgabe 200) stehen
 *   nur noch die sichtbaren im Dokument, und trotzdem ist die ganze Liste
 *   erreichbar.
 *
 * WAS ES NICHT TUT
 * ----------------
 * Es holt keine Daten und kennt keine API. Serverseitiges Blättern bleibt
 * Sache der Seite; sie übergibt dann `proSeite={0}` und blättert selbst.
 *
 * TX3 (18.10.2026): Gruppenzeilen, Summenfuß, Kopfgruppen
 * -------------------------------------------------------
 * - `gruppierung`: Gruppenzeile je Gruppe mit Titel, Anzahl, Zwischensummen,
 *   Farbstreifen (mit Text) und Auf-/Zuklappen. Sortiert wird innerhalb der
 *   Gruppen; die Gruppenfolge bleibt.
 * - `Spalte.fuss`: Summenfuß (und Zwischensumme) je Spalte nach der
 *   Fehlstellenregel in `tabellengruppen.ts` - nie still mit 0 gerechnet.
 * - `Spalte.gruppe`, `kopfZusatz`, `kopfGedreht`: mehrzeiliger Kopf.
 * - Druck: Vor dem Drucken stehen alle Zeilen im Dokument (keine
 *   Virtualisierung) und alle Gruppen sind offen.
 * - `beiZeileKlick`: Die Zeile ist mit Tab erreichbar und mit Eingabe oder
 *   Leertaste auslösbar (S1.10 Nr. 2). Ohne `beiZeileKlick` bleibt das DOM
 *   wie vor TX3 (`DataTable.dom.test.tsx`).
 */

/* ═══════════════════════════════════════════════════════════════════════════
   BEGRIFFE
   ═══════════════════════════════════════════════════════════════════════════ */

export type SortierRichtung = 'auf' | 'ab';

export type SpaltenArt = 'text' | 'zahl' | 'status' | 'verweis' | 'datum';

export interface Spalte<Z> {
  /** Eindeutiger Schlüssel; steht in `data-spalte` und in der Spaltenwahl. */
  schluessel: string;
  /** Überschrift. */
  titel: string;
  /**
   * Einheit der Spalte - steht klein unter der Überschrift, NICHT in jeder
   * Zelle (Abschnitt 15: Wert und Einheit getrennt).
   */
  einheit?: string;
  /**
   * Fachliche Art. Steuert Ausrichtung (`zahl` rechts) und Schreibweise.
   * `status` erwartet in `inhalt` eine `StatusBadge`.
   */
  art?: SpaltenArt;
  /** Der Wert, nach dem sortiert, gesucht und ausgeführt wird. */
  wert?: (zeile: Z) => unknown;
  /** Freie Darstellung der Zelle. Ohne Angabe: der Wert als Text. */
  inhalt?: (zeile: Z) => ReactNode;
  /** Nachkommastellen für `art: 'zahl'`. */
  nachkomma?: number;
  /** Ausrichtung überschreiben. */
  ausrichtung?: 'links' | 'rechts' | 'mitte';
  /** Feste Breite, z. B. `'120px'` oder `'12%'`. */
  breite?: string;
  /** Vorgabe `true`. */
  sortierbar?: boolean;
  /** Vorgabe `false` - Spalte ist beim Öffnen ausgeblendet. */
  versteckt?: boolean;
  /** Vorgabe `false` - Spalte lässt sich in der Spaltenwahl nicht abwählen. */
  immerSichtbar?: boolean;
  /** Erläuterung der Spalte (Überfahren des Kopfes). */
  titelHinweis?: string;

  /**
   * Summenfuß und Zwischensumme der Gruppen (TX3), z. B. `'summe'` oder
   * `{ art: 'summe', schranke: true }` für eine untere Schranke. Die erste
   * sichtbare Spalte trägt den Titel der Fuß- und Gruppenzeilen, ihr Fuß
   * bleibt leer.
   */
  fuss?: FussDefinition;
  /** Kopfgruppen über dem Titel, von oben nach unten (TX3). */
  gruppe?: string | string[];
  /** Zusatzzeile unter dem Titel, z. B. die Spaltennummer (VDI 3814). */
  kopfZusatz?: string;
  /** Titel senkrecht - für viele schmale Spalten (VDI 3814). */
  kopfGedreht?: boolean;
}

export interface KontextEintrag<Z> {
  beschriftung: string;
  beiWahl: (zeile: Z) => void;
  /** Ausgegraut statt versteckt, wenn die Handlung hier nicht geht. */
  deaktiviert?: (zeile: Z) => boolean;
  /** Hebt den Eintrag als gefährlich hervor (Löschen). */
  gefaehrlich?: boolean;
}

export interface Mehrfachauswahl {
  gewaehlt: string[];
  beiAuswahl: (schluessel: string[]) => void;
}

export interface DataTableProps<Z> {
  spalten: Spalte<Z>[];
  zeilen: Z[];
  /** Stabile Kennung je Zeile - Reactschlüssel und Auswahlschlüssel. */
  schluessel: (zeile: Z) => string;

  /** Tabellenname über der Tabelle. */
  name?: string;
  /** Einzeiler unter dem Namen. */
  beschreibung?: string;
  /** Wird statt der Trefferzahl gezeigt, z. B. bei serverseitigem Blättern. */
  trefferText?: string;

  /** Ladezustand (Abschnitt 19). */
  laedt?: boolean;
  /** Ladefortschritt in Prozent - nur wenn gemessen. */
  fortschritt?: number;
  /** Fehler (Abschnitt 21); verdrängt die Tabelle. */
  fehler?: unknown;
  beiErneutVersuchen?: () => void;
  /** Eigener Leerzustand (Abschnitt 20); ohne Angabe ein allgemeiner. */
  leer?: ReactNode;

  /** Suchfeld in der Werkzeugleiste. Vorgabe `true`. */
  suche?: boolean;
  suchePlatzhalter?: string;
  /** Suchbegriff von außen führen (z. B. aus der Adresszeile). */
  suchbegriff?: string;
  beiSuche?: (begriff: string) => void;

  /** Spaltenwahl anbieten. Vorgabe `true`. */
  spaltenwahl?: boolean;
  /** CSV-Ausfuhr anbieten. Vorgabe `false`. */
  ausfuhr?: boolean;
  ausfuhrDateiname?: string;

  /** Anfangssortierung. */
  sortierung?: { spalte: string; richtung: SortierRichtung };
  beiSortierung?: (sortierung: { spalte: string; richtung: SortierRichtung }) => void;

  /** Mehrfachauswahl über Haken in der ersten Spalte. */
  auswahl?: Mehrfachauswahl;
  /** Leiste über der Tabelle, sobald etwas gewählt ist. */
  auswahlAktionen?: ReactNode;

  /** Kontextmenü je Zeile (⋯ in der letzten Spalte). */
  kontextmenue?: KontextEintrag<Z>[];

  /** Zeilen je Seite. `0` (Vorgabe) heißt: kein Blättern. */
  proSeite?: number;

  /** Ab dieser Zeilenzahl wird virtualisiert. Vorgabe 200. */
  virtualisierenAb?: number;
  /** Zeilenhöhe in Pixeln - Grundlage der Virtualisierung. Vorgabe 32. */
  zeilenhoehe?: number;
  /** Höhe des Rollbereichs. Vorgabe 560. */
  hoehe?: number;

  /** Kopf bleibt beim Rollen stehen. Vorgabe `true`. */
  klebenderKopf?: boolean;
  /** Erste Spalte bleibt beim waagerechten Rollen stehen. Vorgabe `false`. */
  klebendeErsteSpalte?: boolean;
  /** Engere Zeilen für sehr breite Tabellen. */
  dicht?: boolean;

  /** Zusätzliche Bedienelemente rechts in der Werkzeugleiste (Filter …). */
  werkzeuge?: ReactNode;

  beiZeileKlick?: (zeile: Z) => void;
  /** Zusätzliche Klasse je Zeile (fachliche Hinterlegung). */
  zeilenKlasse?: (zeile: Z) => string | undefined;
  /** Kennzeichnet die gerade geöffnete Zeile. */
  aktiveZeile?: string;

  /** Gruppenzeilen (TX3). */
  gruppierung?: Gruppierung<Z>;
  /** Beschriftung der Fußzeile. Vorgabe „Summe". */
  fussTitel?: string;

  className?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   RECHNENDE LOGIK - ohne React, deshalb einzeln prüfbar
   ═══════════════════════════════════════════════════════════════════════════ */

/** Rohwert einer Zelle: `wert()` der Spalte, sonst das gleichnamige Feld. */
export function zellenWert<Z>(spalte: Spalte<Z>, zeile: Z): unknown {
  if (spalte.wert) return spalte.wert(zeile);
  return (zeile as Record<string, unknown>)[spalte.schluessel];
}

/** Zelle als angezeigter Text (deutsche Schreibweise, ohne Einheit). */
export function zellenText<Z>(spalte: Spalte<Z>, zeile: Z): string {
  const wert = zellenWert(spalte, zeile);
  if (wert === null || wert === undefined) return '';
  if (typeof wert === 'number') return formatiereZahl(wert, spalte.nachkomma);
  if (wert instanceof Date) return wert.toLocaleDateString('de-DE');
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein';
  return String(wert);
}

/**
 * Vergleich zweier Zellenwerte. Zahlen als Zahlen, Text nach deutschem
 * Alphabet UND mit natürlicher Zahlenfolge (`RLT-9` vor `RLT-10`) - die
 * Kennungen dieser Anwendung sind fast alle so gebaut.
 */
function vergleiche(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), 'de', {
    numeric: true,
    sensitivity: 'base',
  });
}

const fehlt = (w: unknown) => w === null || w === undefined || w === '';

/**
 * Sortiert eine Kopie. Fehlende Werte stehen IMMER am Ende, unabhängig von
 * der Richtung: Ein Loch ist kein großer und kein kleiner Wert.
 */
export function sortiereZeilen<Z>(
  zeilen: Z[],
  spalte: Spalte<Z> | undefined,
  richtung: SortierRichtung,
): Z[] {
  if (!spalte) return zeilen;
  const vorzeichen = richtung === 'ab' ? -1 : 1;

  return [...zeilen].sort((x, y) => {
    const a = zellenWert(spalte, x);
    const b = zellenWert(spalte, y);
    if (fehlt(a) && fehlt(b)) return 0;
    if (fehlt(a)) return 1;
    if (fehlt(b)) return -1;
    return vergleiche(a, b) * vorzeichen;
  });
}

/**
 * Freitextsuche über die übergebenen Spalten. Gesucht wird sowohl in der
 * angezeigten Schreibweise („85.000") als auch im Rohwert („85000") - der
 * Anwender tippt mal so, mal so.
 */
export function filtereZeilen<Z>(
  zeilen: Z[],
  spalten: Spalte<Z>[],
  begriff: string,
): Z[] {
  const suche = begriff.trim().toLowerCase();
  if (suche === '') return zeilen;

  return zeilen.filter((zeile) => spalten.some((spalte) => {
    const angezeigt = zellenText(spalte, zeile).toLowerCase();
    if (angezeigt.includes(suche)) return true;
    const roh = zellenWert(spalte, zeile);
    return roh !== null && roh !== undefined
      && String(roh).toLowerCase().includes(suche);
  }));
}

/** Ein CSV-Feld schützen (Semikolontrennung, deutsche Tabellenkalkulation). */
function csvFeld(text: string): string {
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * CSV der sichtbaren Spalten und Zeilen. Zahlen stehen UNGRUPPIERT mit
 * Dezimalkomma darin: Ein Tausenderpunkt macht aus 85.000 in jeder
 * Tabellenkalkulation eine 85.
 */
export function alsCsv<Z>(
  spalten: Spalte<Z>[],
  zeilen: Z[],
  zusatz?: {
    /** Gruppen: führende Spalte „Gruppe", je Gruppe ihre Zeilen und eine Zwischensummenzeile. */
    gruppierung?: Gruppierung<Z>;
    /** Alle Zeilen (ungefiltert) - Grundlage der Gruppenfolge. Vorgabe: `zeilen`. */
    alle?: Z[];
    fussTitel?: string;
    /** Die Liste ist gefiltert: feste Fußwerte des Laufs gelten dann nicht. */
    gefiltert?: boolean;
  },
): string {
  const kopfFelder = spalten
    .map((s) => csvFeld(s.einheit ? `${s.titel} [${s.einheit}]` : s.titel));

  const zeileCsv = (zeile: Z) => spalten.map((spalte) => {
    const wert = zellenWert(spalte, zeile);
    if (wert === null || wert === undefined) return '';
    if (typeof wert === 'number') return csvFeld(String(wert).replace('.', ','));
    return csvFeld(zellenText(spalte, zeile));
  });

  const g = zusatz?.gruppierung;
  const mitFuss = spalten.some((s) => s.fuss !== undefined);

  // Ohne TX3-Angaben genau wie vorher.
  if (!g && !mitFuss) {
    return [kopfFelder.join(';'), ...zeilen.map((z) => zeileCsv(z).join(';'))].join('\n');
  }

  // Eine Fuß- oder Zwischensummenzeile: Beschriftung (mit Vermerk) vorn,
  // Zahlen ungruppiert in ihren Spalten, wie die Datenzeilen.
  const zusammen = (titel: string, menge: Z[], gruppe: boolean, vorn: boolean) => {
    const werte = spalten.map((s, i) => {
      // Wie auf der Seite: Die erste Spalte trägt die Beschriftung, ihr Fuß bleibt leer.
      if (i === 0 || s.fuss === undefined) return null;
      // Ein fester Wert des Laufs gilt für die ganze Liste, nie für eine Gruppe.
      if (gruppe && istFest(s.fuss)) return null;
      return fussFuer(s, menge, zusatz?.gefiltert ?? false);
    });
    const vermerk = fehlstellenVermerk(spalten.flatMap((s, i) => {
      const w = werte[i];
      return w ? [{ titel: s.titel, wert: w }] : [];
    }));
    const beschriftung = vermerk ? `${titel} (${vermerk})` : titel;
    const felder = werte.map((w) => (w === null || w.wert === null ? '' : String(w.wert).replace('.', ',')));
    if (vorn) return [csvFeld(beschriftung), ...felder].join(';');
    felder[0] = csvFeld(beschriftung);
    return felder.join(';');
  };

  const fussTitel = zusatz?.fussTitel ?? 'Summe';

  if (!g) {
    return [kopfFelder.join(';'), ...zeilen.map((z) => zeileCsv(z).join(';')),
      zusammen(fussTitel, zeilen, false, false)].join('\n');
  }

  const gruppen = gruppiereZeilen(zusatz?.alle ?? zeilen, zeilen, g);
  const aus = [['Gruppe', ...kopfFelder].join(';')];
  for (const gr of gruppen) {
    for (const z of gr.zeilen) aus.push([csvFeld(gr.titel), ...zeileCsv(z)].join(';'));
    if (mitFuss) aus.push(zusammen(`${gr.titel} – Zwischensumme`, gr.zeilen, true, true));
  }
  if (mitFuss) aus.push(zusammen(fussTitel, zeilen, false, true));
  return aus.join('\n');
}

function istFest(d: FussDefinition): boolean {
  return typeof d === 'object' && 'fest' in d;
}

/** Fußwert einer Spalte; ein fester Wert des Laufs gilt nicht für eine gefilterte Liste. */
function fussFuer<Z>(s: Spalte<Z>, menge: Z[], gefiltert: boolean): Fusswert | null {
  if (s.fuss === undefined) return null;
  if (istFest(s.fuss) && gefiltert) return null;
  return fussWert(s.fuss, menge.map((z) => zellenWert(s, z)));
}

/**
 * Das Fenster der tatsächlich dargestellten Zeilen.
 *
 * Unterhalb von `schwelle` Zeilen wird gar nicht virtualisiert - die
 * Rechnerei kostet dann mehr, als sie spart, und ein einfaches Dokument
 * lässt sich im Browser durchsuchen (Strg+F).
 */
export function sichtbaresFenster(
  gesamt: number,
  scrollOben: number,
  sichthoehe: number,
  zeilenhoehe: number,
  schwelle: number,
): { von: number; bis: number } {
  if (gesamt <= schwelle) return { von: 0, bis: gesamt };

  const sichtbar = Math.max(10, Math.ceil(sichthoehe / zeilenhoehe));
  const puffer = Math.max(10, Math.ceil(sichtbar / 2));
  const erste = Math.floor(Math.max(0, scrollOben) / zeilenhoehe);

  const hoechstesVon = Math.max(0, gesamt - sichtbar - 2 * puffer);
  const von = Math.min(Math.max(0, erste - puffer), hoechstesVon);
  const bis = Math.min(gesamt, von + sichtbar + 2 * puffer);

  return { von, bis };
}

/* ═══════════════════════════════════════════════════════════════════════════
   DARSTELLUNG
   ═══════════════════════════════════════════════════════════════════════════ */

function ausrichtungVon<Z>(spalte: Spalte<Z>): 'links' | 'rechts' | 'mitte' {
  if (spalte.ausrichtung) return spalte.ausrichtung;
  return spalte.art === 'zahl' ? 'rechts' : 'links';
}

export function DataTable<Z>({
  spalten, zeilen, schluessel,
  name, beschreibung, trefferText,
  laedt, fortschritt, fehler, beiErneutVersuchen, leer,
  suche = true, suchePlatzhalter, suchbegriff, beiSuche,
  spaltenwahl = true, ausfuhr = false, ausfuhrDateiname,
  sortierung, beiSortierung,
  auswahl, auswahlAktionen,
  kontextmenue,
  proSeite = 0,
  virtualisierenAb = 200, zeilenhoehe = 32, hoehe = 560,
  klebenderKopf = true, klebendeErsteSpalte = false, dicht = false,
  werkzeuge,
  beiZeileKlick, zeilenKlasse, aktiveZeile,
  gruppierung, fussTitel,
  className,
}: DataTableProps<Z>) {
  /* --- Zustand: gesteuert von außen, sonst hier ------------------------- */
  const [sucheIntern, setSucheIntern] = useState('');
  const begriff = suchbegriff ?? sucheIntern;
  const setzeSuche = (wert: string) => {
    if (beiSuche) beiSuche(wert); else setSucheIntern(wert);
  };

  const [sortIntern, setSortIntern] = useState<
  { spalte: string; richtung: SortierRichtung } | null>(sortierung ?? null);
  const sort = sortierung ?? sortIntern;
  const setzeSortierung = (s: { spalte: string; richtung: SortierRichtung }) => {
    if (beiSortierung) beiSortierung(s); else setSortIntern(s);
  };

  const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(
    () => new Set(spalten.filter((s) => s.versteckt).map((s) => s.schluessel)),
  );
  const [spaltenwahlOffen, setSpaltenwahlOffen] = useState(false);
  const [seite, setSeite] = useState(0);
  /**
   * Das Zeilenmenü merkt sich seinen Platz im Fenster, nicht in der Zelle:
   * Der Tabellenkörper rollt (`overflow: auto`), und ein Menü in der
   * letzten Zeile würde darin abgeschnitten - ohne Möglichkeit, es zu
   * sehen, weil unterhalb kein Rollweg mehr ist. `position: fixed` mit
   * gemessener Lage entkommt dem, ohne ein Portal einzuführen.
   */
  const [menueZeile, setMenueZeile] = useState<
  { id: string; oben: number; rechts: number } | null>(null);

  /**
   * Gruppen, die zu sind. `'alle'` mit Ausnahmen, damit „alle zu" auch für
   * Gruppen gilt, die erst nach dem ersten Laden dazukommen.
   */
  const [zuGruppen, setZuGruppen] = useState<{ alle: boolean; ausnahmen: Set<string> }>(() => (
    gruppierung?.zugeklappt === 'alle'
      ? { alle: true, ausnahmen: new Set() }
      : { alle: false, ausnahmen: new Set(gruppierung?.zugeklappt ?? []) }
  ));

  /**
   * DRUCK (TX3): Eine virtualisierte Tabelle hätte auf Papier nur das
   * sichtbare Fenster, eine zugeklappte Gruppe nur ihre Kopfzeile. Vor dem
   * Drucken stehen deshalb ALLE Zeilen im Dokument und alle Gruppen offen.
   * `flushSync`, weil der Browser das Druckbild direkt nach dem Ereignis
   * aufnimmt - ein später geplantes Neuzeichnen käme zu spät.
   */
  const [drucken, setDrucken] = useState(false);
  useEffect(() => {
    const vor = () => flushSync(() => setDrucken(true));
    const nach = () => setDrucken(false);
    window.addEventListener('beforeprint', vor);
    window.addEventListener('afterprint', nach);
    return () => {
      window.removeEventListener('beforeprint', vor);
      window.removeEventListener('afterprint', nach);
    };
  }, []);

  // Ein offenes Menü schließt beim Klick daneben, bei ESC und beim Rollen.
  // Ohne das bleibt es stehen, während die Zeile darunter wegrollt.
  useEffect(() => {
    if (!spaltenwahlOffen && !menueZeile) return undefined;

    const beiKlick = (e: MouseEvent) => {
      // `e.target` ist nicht immer ein Element (Dokument, Textknoten) -
      // `closest` gäbe es dort nicht, und der Fehler bliebe stumm.
      const ziel = e.target instanceof Element ? e.target : null;
      if (ziel?.closest('.m-tabelle-spaltenwahl')) return;
      if (ziel?.closest('.m-tabelle-menue')) return;
      setSpaltenwahlOffen(false);
      setMenueZeile(null);
    };
    const beiTasteImDokument = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSpaltenwahlOffen(false);
      setMenueZeile(null);
    };
    const beiFensterRollen = () => setMenueZeile(null);

    document.addEventListener('mousedown', beiKlick);
    document.addEventListener('keydown', beiTasteImDokument);
    window.addEventListener('scroll', beiFensterRollen, true);
    return () => {
      document.removeEventListener('mousedown', beiKlick);
      document.removeEventListener('keydown', beiTasteImDokument);
      window.removeEventListener('scroll', beiFensterRollen, true);
    };
  }, [spaltenwahlOffen, menueZeile]);

  /* --- Spalten --------------------------------------------------------- */
  const sichtbareSpalten = useMemo(
    () => spalten.filter((s) => !ausgeblendet.has(s.schluessel)),
    [spalten, ausgeblendet],
  );

  /* --- Suchen, sortieren, blättern ------------------------------------- */
  const gefiltert = useMemo(
    () => filtereZeilen(zeilen, sichtbareSpalten, begriff),
    [zeilen, sichtbareSpalten, begriff],
  );

  const sortiert = useMemo(() => {
    const spalte = sort ? spalten.find((s) => s.schluessel === sort.spalte) : undefined;
    return sortiereZeilen(gefiltert, spalte, sort?.richtung ?? 'auf');
  }, [gefiltert, sort, spalten]);

  const seitenzahl = proSeite > 0 ? Math.max(1, Math.ceil(sortiert.length / proSeite)) : 1;
  const sichereSeite = Math.min(seite, seitenzahl - 1);
  const aufSeite = proSeite > 0
    ? sortiert.slice(sichereSeite * proSeite, (sichereSeite + 1) * proSeite)
    : sortiert;

  // Beim Suchen zurück auf die erste Seite - sonst steht man vor einer
  // leeren Seite 7 eines Ergebnisses mit zwei Treffern.
  useEffect(() => { setSeite(0); }, [begriff, proSeite]);

  /* --- Gruppen (TX3) ---------------------------------------------------- */
  // Die Zeilen werden NACH der Sortierung geteilt: Die Gruppen behalten ihre
  // Folge, innerhalb einer Gruppe gilt die Sortierung der Tabelle.
  const gruppen = useMemo<Gruppe<Z>[] | null>(
    () => (gruppierung ? gruppiereZeilen(zeilen, sortiert, gruppierung) : null),
    [gruppierung, zeilen, sortiert],
  );
  const sucheAktiv = begriff.trim() !== '';
  const istOffen = (s: string) => {
    // Beim Suchen und Drucken sind alle Gruppen offen: Ein Treffer in einer
    // zugeklappten Gruppe wäre sonst nicht zu sehen.
    if (sucheAktiv || drucken || gruppierung?.aufklappbar === false) return true;
    return zuGruppen.alle ? zuGruppen.ausnahmen.has(s) : !zuGruppen.ausnahmen.has(s);
  };
  const gruppeUmschalten = (s: string) => setZuGruppen((alt) => {
    const neu = new Set(alt.ausnahmen);
    if (neu.has(s)) neu.delete(s); else neu.add(s);
    return { alle: alt.alle, ausnahmen: neu };
  });
  const elemente: Anzeigezeile<Z>[] | null = gruppen
    ? anzeigezeilen(gruppen, istOffen,
      proSeite > 0 ? sichereSeite * proSeite : 0,
      proSeite > 0 ? (sichereSeite + 1) * proSeite : Infinity)
    : null;

  /* --- Virtualisierung -------------------------------------------------- */
  const rollbereich = useRef<HTMLDivElement | null>(null);
  const [scrollOben, setScrollOben] = useState(0);
  const anzahlDargestellt = elemente ? elemente.length : aufSeite.length;
  const virtualisiert = proSeite === 0 && anzahlDargestellt > virtualisierenAb && !drucken;

  const fenster = virtualisiert
    ? sichtbaresFenster(anzahlDargestellt, scrollOben, hoehe, zeilenhoehe, virtualisierenAb)
    : { von: 0, bis: anzahlDargestellt };

  const beiRollen = useCallback(() => {
    const el = rollbereich.current;
    if (el) setScrollOben(el.scrollTop);
  }, []);

  const gezeigt = aufSeite.slice(fenster.von, fenster.bis);

  /* --- Auswahl ---------------------------------------------------------- */
  const gewaehlt = useMemo(
    () => new Set(auswahl?.gewaehlt ?? []),
    [auswahl?.gewaehlt],
  );
  const alleAufSeiteGewaehlt = aufSeite.length > 0
    && aufSeite.every((z) => gewaehlt.has(schluessel(z)));

  const umschalten = (id: string) => {
    if (!auswahl) return;
    const neu = new Set(gewaehlt);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    auswahl.beiAuswahl([...neu]);
  };

  const alleUmschalten = () => {
    if (!auswahl) return;
    const neu = new Set(gewaehlt);
    for (const z of aufSeite) {
      const id = schluessel(z);
      if (alleAufSeiteGewaehlt) neu.delete(id); else neu.add(id);
    }
    auswahl.beiAuswahl([...neu]);
  };

  /* --- Sortierung umschalten -------------------------------------------- */
  const kopfKlick = (spalte: Spalte<Z>) => {
    if (spalte.sortierbar === false) return;
    const richtung: SortierRichtung = sort?.spalte === spalte.schluessel
      && sort.richtung === 'auf' ? 'ab' : 'auf';
    setzeSortierung({ spalte: spalte.schluessel, richtung });
  };

  /* --- Fuß (TX3) -------------------------------------------------------- */
  const gefiltertAnzeige = sortiert.length !== zeilen.length;
  // Die erste sichtbare Spalte trägt die Beschriftung der Fuß- und
  // Gruppenzeilen; Zusammenfassungen stehen ab der ersten Spalte mit Fuß.
  const ersteFussSpalte = sichtbareSpalten.findIndex((s, i) => i > 0 && s.fuss !== undefined);
  const mitFuss = ersteFussSpalte > 0;

  /* --- CSV -------------------------------------------------------------- */
  const csvHerunterladen = () => {
    const csv = gruppierung || mitFuss
      ? alsCsv(sichtbareSpalten, sortiert, {
        gruppierung, alle: zeilen, fussTitel, gefiltert: gefiltertAnzeige,
      })
      : alsCsv(sichtbareSpalten, sortiert);
    // BOM: ohne ihn liest Excel die Umlaute der Kopfzeile falsch.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = ausfuhrDateiname ?? `${(name ?? 'tabelle').replace(/\W+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* --- Zustände, die die Tabelle verdrängen ----------------------------- */
  const spaltenzahl = sichtbareSpalten.length
    + (auswahl ? 1 : 0) + (kontextmenue ? 1 : 0);

  const werkzeugleiste = (
    <div className="m-tabelle-werkzeuge">
      {suche && (
        <label className="m-tabelle-suche">
          <span className="m-nur-vorlesen">
            {name ? `${name} durchsuchen` : 'Tabelle durchsuchen'}
          </span>
          <input
            type="search"
            value={begriff}
            placeholder={suchePlatzhalter ?? 'Suchen …'}
            onChange={(e) => setzeSuche(e.target.value)}
          />
        </label>
      )}
      {werkzeuge}
      {spaltenwahl && (
        <div className="m-tabelle-spaltenwahl">
          <button
            type="button"
            className="m-knopf m-knopf-still"
            aria-haspopup="true"
            aria-expanded={spaltenwahlOffen}
            onClick={() => setSpaltenwahlOffen((o) => !o)}
          >
            Spalten
            <span className="m-knopf-pfeil" aria-hidden="true">▾</span>
          </button>
          {spaltenwahlOffen && (
            <div className="m-tabelle-spaltenliste" role="group" aria-label="Spalten wählen">
              {spalten.map((s) => (
                <label key={s.schluessel} className="m-tabelle-spaltenpunkt">
                  <input
                    type="checkbox"
                    value={s.schluessel}
                    checked={!ausgeblendet.has(s.schluessel)}
                    disabled={s.immerSichtbar}
                    onChange={() => setAusgeblendet((alt) => {
                      const neu = new Set(alt);
                      if (neu.has(s.schluessel)) neu.delete(s.schluessel);
                      else neu.add(s.schluessel);
                      return neu;
                    })}
                  />
                  {s.titel}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      {ausfuhr && (
        <button type="button" className="m-knopf m-knopf-still" onClick={csvHerunterladen}>
          CSV-Export
        </button>
      )}
    </div>
  );

  const kopfbereich = (
    <div className="m-tabelle-kopf">
      <div>
        {name && (
          <h3 className="m-tabelle-name">
            {name}
            <span className="m-tabelle-treffer">
              {trefferText ?? `${formatiereZahl(sortiert.length)} ${
                sortiert.length === 1 ? 'Eintrag' : 'Einträge'}${
                sortiert.length !== zeilen.length
                  ? ` von ${formatiereZahl(zeilen.length)}` : ''}`}
            </span>
          </h3>
        )}
        {!name && (
          <span className="m-tabelle-treffer">
            {trefferText ?? `${formatiereZahl(sortiert.length)} ${
              sortiert.length === 1 ? 'Eintrag' : 'Einträge'}`}
          </span>
        )}
        {beschreibung && <p className="m-tabelle-beschreibung">{beschreibung}</p>}
      </div>
      {werkzeugleiste}
    </div>
  );

  if (fehler !== undefined && fehler !== null) {
    return (
      <div className={`m-tabelle-rahmen${className ? ` ${className}` : ''}`}>
        {kopfbereich}
        <ErrorState
          meldung="Die Tabelle konnte nicht aufgebaut werden."
          technisch={fehler}
          beiErneutVersuchen={beiErneutVersuchen}
        />
      </div>
    );
  }

  if (laedt) {
    return (
      <div className={`m-tabelle-rahmen${className ? ` ${className}` : ''}`}>
        {kopfbereich}
        <LoadingState
          text={name ? `${name} werden geladen …` : 'wird geladen …'}
          zeilen={6}
          spalten={Math.min(6, Math.max(1, sichtbareSpalten.length))}
          fortschritt={fortschritt}
        />
      </div>
    );
  }

  /* --- Tabelle ---------------------------------------------------------- */
  const leerzustand = sortiert.length === 0 && (
    begriff.trim() !== ''
      ? (
        <EmptyState
          titel="Kein Eintrag passt zur Suche."
          erklaerung={<>Gesucht wurde nach „{begriff}" in allen sichtbaren Spalten.</>}
          aktion={(
            <button type="button" className="m-knopf" onClick={() => setzeSuche('')}>
              Suche zurücksetzen
            </button>
          )}
          klein
        />
      )
      : leer ?? (
        <EmptyState
          titel="Noch kein Eintrag vorhanden."
          erklaerung="Sobald Daten aufgenommen oder berechnet wurden, stehen sie hier."
          klein
        />
      )
  );

  const zeilenKlassen = (zeile: Z, id: string) => {
    const k = ['m-tabelle-zeile'];
    if (gewaehlt.has(id)) k.push('m-tabelle-zeile-gewaehlt');
    if (aktiveZeile === id) k.push('m-tabelle-zeile-aktiv');
    if (beiZeileKlick) k.push('m-tabelle-zeile-klickbar');
    const eigen = zeilenKlasse?.(zeile);
    if (eigen) k.push(eigen);
    return k.join(' ');
  };

  /**
   * S1.10 Nr. 2: Eine anklickbare Zeile ist mit Tab erreichbar und mit
   * Eingabe oder Leertaste auslösbar. Nur Tasten auf der Zeile selbst - ein
   * Haken oder Knopf darin behält seine eigene Bedienung.
   */
  const zeileTaste = (zeile: Z) => (e: ReactKeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    beiZeileKlick?.(zeile);
  };

  /* --- Kopf ------------------------------------------------------------- */
  const kopf = kopfzeilen(sichtbareSpalten);
  const mehrzeilig = kopf.tiefe > 0 || kopf.zusatz;
  const kopfreihen = kopf.zeilen.length + (kopf.zusatz ? 1 : 0);

  const titelzelle = (s: Spalte<Z>, rowSpan?: number) => {
    const istSortiert = sort?.spalte === s.schluessel;
    const sortierbar = s.sortierbar !== false;
    return (
      <th
        key={s.schluessel}
        scope="col"
        rowSpan={rowSpan}
        data-spalte={s.schluessel}
        className={`m-spalte-${ausrichtungVon(s)}${s.kopfGedreht ? ' m-tabelle-kopf-gedreht' : ''}`}
        style={s.breite ? { width: s.breite } : undefined}
        aria-sort={!sortierbar ? undefined
          : istSortiert
            ? (sort!.richtung === 'auf' ? 'ascending' : 'descending')
            : 'none'}
        title={s.titelHinweis}
      >
        {sortierbar ? (
          <button
            type="button"
            className="m-tabelle-kopfknopf"
            onClick={() => kopfKlick(s)}
          >
            <span className="m-tabelle-kopftitel">{s.titel}</span>
            {s.einheit && (
              <span className="m-tabelle-kopfeinheit">{s.einheit}</span>
            )}
            <span className="m-tabelle-sortzeichen" aria-hidden="true">
              {istSortiert ? (sort!.richtung === 'auf' ? '▲' : '▼') : '⇅'}
            </span>
          </button>
        ) : (
          <span className="m-tabelle-kopfknopf">
            <span className="m-tabelle-kopftitel">{s.titel}</span>
            {s.einheit && (
              <span className="m-tabelle-kopfeinheit">{s.einheit}</span>
            )}
          </span>
        )}
      </th>
    );
  };

  const auswahlKopf = (rowSpan?: number) => (
    <th scope="col" rowSpan={rowSpan} className="m-tabelle-auswahl">
      <input
        type="checkbox"
        checked={alleAufSeiteGewaehlt}
        onChange={alleUmschalten}
        aria-label="Alle sichtbaren Zeilen wählen"
      />
    </th>
  );

  const menueKopf = (rowSpan?: number) => (
    <th scope="col" rowSpan={rowSpan} className="m-tabelle-menuespalte">
      <span className="m-nur-vorlesen">Zeilenmenü</span>
    </th>
  );

  const tabellenkopf = !mehrzeilig ? (
    <thead>
      <tr>
        {auswahl && auswahlKopf()}
        {sichtbareSpalten.map((s) => titelzelle(s))}
        {kontextmenue && menueKopf()}
      </tr>
    </thead>
  ) : (
    <thead>
      {kopf.zeilen.map((reihe, r) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={`k${r}`} className={r < kopf.tiefe ? 'm-tabelle-kopfgruppen' : undefined}>
          {r === 0 && auswahl && auswahlKopf(kopfreihen)}
          {reihe.map((zelle) => (zelle.art === 'gruppe' ? (
            <th
              key={`g${r}-${zelle.erste}`}
              scope="colgroup"
              colSpan={zelle.colSpan}
              className="m-tabelle-kopfgruppe"
            >
              {zelle.titel}
            </th>
          ) : titelzelle(zelle.spalte, zelle.rowSpan > 1 ? zelle.rowSpan : undefined)))}
          {r === 0 && kontextmenue && menueKopf(kopfreihen)}
        </tr>
      ))}
      {kopf.zusatz && (
        <tr className="m-tabelle-kopfzusatz">
          {sichtbareSpalten.map((s) => (
            <th key={s.schluessel} scope="col" className={`m-spalte-${ausrichtungVon(s)}`}>
              {s.kopfZusatz ?? ''}
            </th>
          ))}
        </tr>
      )}
    </thead>
  );

  /* --- Zeilen ----------------------------------------------------------- */
  const datenzeile = (zeile: Z) => {
    const id = schluessel(zeile);
    return (
      <tr
        key={id}
        className={zeilenKlassen(zeile, id)}
        style={virtualisiert ? { height: zeilenhoehe } : undefined}
        onClick={beiZeileKlick ? () => beiZeileKlick(zeile) : undefined}
        tabIndex={beiZeileKlick ? 0 : undefined}
        onKeyDown={beiZeileKlick ? zeileTaste(zeile) : undefined}
      >
        {auswahl && (
          <td className="m-tabelle-auswahl">
            <input
              type="checkbox"
              checked={gewaehlt.has(id)}
              onChange={() => umschalten(id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Zeile ${id} wählen`}
            />
          </td>
        )}
        {sichtbareSpalten.map((s) => (
          <td
            key={s.schluessel}
            className={`m-spalte-${ausrichtungVon(s)}${
              s.art === 'zahl' ? ' m-spalte-zahl' : ''}`}
          >
            {s.inhalt ? s.inhalt(zeile) : zellenText(s, zeile)}
          </td>
        ))}
        {kontextmenue && (
          <td className="m-tabelle-menuespalte">
            <div className="m-tabelle-menue">
              <button
                type="button"
                className="m-knopf m-knopf-nur-zeichen"
                aria-haspopup="menu"
                aria-expanded={menueZeile?.id === id}
                aria-label="Weitere Handlungen zu dieser Zeile"
                onClick={(e) => {
                  e.stopPropagation();
                  const k = e.currentTarget.getBoundingClientRect();
                  setMenueZeile((m) => (m?.id === id ? null : {
                    id,
                    oben: k.bottom + 2,
                    rechts: window.innerWidth - k.right,
                  }));
                }}
              >
                <span aria-hidden="true">⋯</span>
              </button>
              {menueZeile?.id === id && (
                <div
                  className="m-tabelle-menueflaeche"
                  role="menu"
                  style={{
                    position: 'fixed',
                    top: menueZeile.oben,
                    right: menueZeile.rechts,
                  }}
                >
                  {kontextmenue.map((e) => (
                    <button
                      key={e.beschriftung}
                      type="button"
                      role="menuitem"
                      className={e.gefaehrlich ? 'm-menuepunkt m-menuepunkt-gefahr' : 'm-menuepunkt'}
                      disabled={e.deaktiviert?.(zeile)}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setMenueZeile(null);
                        e.beiWahl(zeile);
                      }}
                    >
                      {e.beschriftung}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  /* --- Zusammenfassungen (Gruppenzeile, Fuß) ----------------------------- */
  const beschriftungsbreite = (auswahl ? 1 : 0)
    + (mitFuss ? ersteFussSpalte : sichtbareSpalten.length);

  const zusammenfassungszellen = (menge: Z[], inGruppe: boolean) => {
    if (!mitFuss) return null;
    return sichtbareSpalten.slice(ersteFussSpalte).map((s) => {
      const w = s.fuss === undefined || (inGruppe && istFest(s.fuss))
        ? null : fussFuer(s, menge, gefiltertAnzeige);
      const luecke = w === null ? null : fehlstellenText(w);
      return (
        <td
          key={s.schluessel}
          className={`m-spalte-${ausrichtungVon(s)} m-spalte-zahl m-tabelle-fusszelle`}
          data-spalte={s.schluessel}
        >
          {w !== null && (
            <span className="m-tabelle-fusswert">
              {fussText(w, s.fuss !== undefined && fussArt(s.fuss) === 'anzahl' ? 0 : s.nachkomma)}
            </span>
          )}
          {luecke && <span className="m-tabelle-fehlstellen">{luecke}</span>}
          {w === null && s.fuss !== undefined && !inGruppe && istFest(s.fuss) && (
            <span className="m-tabelle-fehlstellen">nur ungefiltert</span>
          )}
        </td>
      );
    });
  };

  const gruppenzeile = (el: Extract<Anzeigezeile<Z>, { art: 'gruppe' }>) => {
    const gr = el.gruppe;
    const marke = gruppierung?.markierung?.(gr.schluessel);
    const aufklappbar = gruppierung?.aufklappbar !== false;
    const text = gruppenBeschriftung(gr);
    return (
      <tr
        key={`gruppe-${gr.schluessel}`}
        className="m-tabelle-gruppenzeile"
        data-gruppe={gr.schluessel}
        style={virtualisiert ? { height: zeilenhoehe } : undefined}
      >
        <th
          scope="rowgroup"
          colSpan={beschriftungsbreite}
          className={`m-tabelle-gruppentitel${marke ? ` m-reihe-${marke}` : ''}`}
        >
          {aufklappbar ? (
            <button
              type="button"
              className="m-tabelle-gruppenknopf"
              aria-expanded={el.offen}
              onClick={() => gruppeUmschalten(gr.schluessel)}
            >
              <span className="m-tabelle-gruppenzeichen" aria-hidden="true">{el.offen ? '▾' : '▸'}</span>
              <span className="m-tabelle-gruppenname">{text}</span>
            </button>
          ) : <span className="m-tabelle-gruppenname">{text}</span>}
          {el.fortsetzung && <span className="m-tabelle-fortsetzung">Fortsetzung</span>}
          {gruppierung?.zusatz && (
            <span className="m-tabelle-gruppenzusatz">{gruppierung.zusatz(gr.schluessel, gr.zeilen)}</span>
          )}
        </th>
        {zusammenfassungszellen(gr.zeilen, true)}
        {kontextmenue && <td className="m-tabelle-menuespalte" />}
      </tr>
    );
  };

  const fussTitelText = fussTitel ?? 'Summe';
  const tabellenfuss = mitFuss && (
    <tfoot>
      <tr className="m-tabelle-summe">
        <th scope="row" colSpan={beschriftungsbreite} className="m-tabelle-summe-titel">
          {gefiltertAnzeige
            ? `${fussTitelText} der gezeigten ${formatiereZahl(sortiert.length)} von ${
              formatiereZahl(zeilen.length)}`
            : fussTitelText}
        </th>
        {zusammenfassungszellen(sortiert, false)}
        {kontextmenue && <td className="m-tabelle-menuespalte" />}
      </tr>
    </tfoot>
  );

  /* --- Körper ----------------------------------------------------------- */
  const luecke = (hoeheZeilen: number, k: string) => (
    <tr aria-hidden="true" className="m-tabelle-luecke" key={k}>
      <td colSpan={spaltenzahl} style={{ height: hoeheZeilen * zeilenhoehe }} />
    </tr>
  );

  let koerper: ReactNode;
  if (!elemente) {
    koerper = (
      <tbody>
        {fenster.von > 0 && (
          <tr aria-hidden="true" className="m-tabelle-luecke">
            <td colSpan={spaltenzahl} style={{ height: fenster.von * zeilenhoehe }} />
          </tr>
        )}

        {gezeigt.map((zeile) => datenzeile(zeile))}

        {fenster.bis < aufSeite.length && (
          <tr aria-hidden="true" className="m-tabelle-luecke">
            <td
              colSpan={spaltenzahl}
              style={{ height: (aufSeite.length - fenster.bis) * zeilenhoehe }}
            />
          </tr>
        )}
      </tbody>
    );
  } else {
    // Je Gruppe ein `<tbody>` (Zeilengruppe für `scope="rowgroup"`). Im
    // virtualisierten Fenster beginnt der erste Abschnitt u. U. mitten in
    // einer Gruppe - dann ohne Gruppenzeile; die Platzhalter stehen in
    // eigenen, für Vorlesehilfen verborgenen Abschnitten.
    const fensterElemente = elemente.slice(fenster.von, fenster.bis);
    const abschnitte: { schluessel: string; zeilen: ReactNode[] }[] = [];
    for (const el of fensterElemente) {
      if (el.art === 'gruppe' || abschnitte.length === 0) {
        abschnitte.push({
          schluessel: el.art === 'gruppe' ? el.gruppe.schluessel : `${el.gruppe ?? ''}-fortsetzung`,
          zeilen: [],
        });
      }
      abschnitte[abschnitte.length - 1].zeilen.push(
        el.art === 'gruppe' ? gruppenzeile(el) : datenzeile(el.zeile),
      );
    }
    koerper = (
      <>
        {fenster.von > 0 && <tbody aria-hidden="true">{luecke(fenster.von, 'oben')}</tbody>}
        {abschnitte.map((a) => (
          <tbody key={a.schluessel} className="m-tabelle-gruppe">{a.zeilen}</tbody>
        ))}
        {fenster.bis < elemente.length && (
          <tbody aria-hidden="true">{luecke(elemente.length - fenster.bis, 'unten')}</tbody>
        )}
      </>
    );
  }

  return (
    <div className={`m-tabelle-rahmen${className ? ` ${className}` : ''}`}>
      {kopfbereich}

      {auswahl && gewaehlt.size > 0 && (
        <div className="m-tabelle-auswahlleiste" role="status">
          <span>{formatiereZahl(gewaehlt.size)} gewählt</span>
          {auswahlAktionen}
          <button
            type="button"
            className="m-knopf m-knopf-still"
            onClick={() => auswahl.beiAuswahl([])}
          >
            Auswahl aufheben
          </button>
        </div>
      )}

      {sortiert.length === 0 ? leerzustand : (
        <div
          className={`m-tabelle-koerper${virtualisiert ? ' m-tabelle-rollend' : ''}`}
          ref={rollbereich}
          onScroll={virtualisiert ? beiRollen : undefined}
          style={virtualisiert ? { maxHeight: hoehe } : undefined}
        >
          <table
            className={[
              'm-tabelle',
              dicht ? 'm-tabelle-dicht' : '',
              klebenderKopf ? 'm-tabelle-klebkopf' : '',
              klebendeErsteSpalte ? 'm-tabelle-klebspalte' : '',
              mehrzeilig ? 'm-tabelle-mehrkopf' : '',
              elemente ? 'm-tabelle-gruppiert' : '',
            ].filter(Boolean).join(' ')}
          >
            {tabellenkopf}
            {koerper}
            {tabellenfuss}
          </table>
        </div>
      )}

      {proSeite > 0 && seitenzahl > 1 && (
        <div className="m-tabelle-blaettern">
          <button
            type="button"
            className="m-knopf m-knopf-still m-tabelle-zurueck"
            disabled={sichereSeite === 0}
            onClick={() => setSeite((s) => Math.max(0, s - 1))}
          >
            ← Zurück
          </button>
          <span className="m-tabelle-blaettern-stand" aria-live="polite">
            Seite {sichereSeite + 1} von {seitenzahl}
          </span>
          <button
            type="button"
            className="m-knopf m-knopf-still m-tabelle-weiter"
            disabled={sichereSeite >= seitenzahl - 1}
            onClick={() => setSeite((s) => Math.min(seitenzahl - 1, s + 1))}
          >
            Weiter →
          </button>
        </div>
      )}

      {virtualisiert && (
        <p className="m-tabelle-fuss">
          {formatiereZahl(anzahlDargestellt)} Zeilen; dargestellt werden nur die
          sichtbaren. Rollen Sie in der Tabelle, um die übrigen zu sehen.
        </p>
      )}
    </div>
  );
}

